import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'
import { useUser } from '../generic/hooks/useUser'
import VisionOverlay from './VisionOverlay'
import VisionChip from './VisionChip'
import VisionCrowdWarning from './VisionCrowdWarning'
import { getVisionScreens, getVisionSession, leaveVision, sendVisionFrame } from './visionApi'
import { VisionContext } from './visionContext'
import { openVisionCamera } from './visionCamera'
import { clearVisionMark, hasVisionMark, setVisionMark } from './visionMark'
import { subscribeVisionSignals } from './visionSignals'
import { COVERED_WITHOUT_STATE, NOT_WATCHED_STATE, readVisionState, sameVisionState } from './visionState'
import { openVisionChannel, runAsEmitter } from './visionTabs'

// RN12 base rhythm: only used before Sun has said how long to wait (a failed frame request).
const FRAME_RETRY_MS = 500

// C7.1 v2: with no sensitive boundary mounted for this long, the guard asks Sun to end the vigilance (RN9).
const LEAVE_AFTER_MS = 2000

// C16.7 (RN144): a session without vigilance asks for its state this often, to get in as soon as it is given a slot; without
// a camera it also tries the camera again then (C16.10, S340).
const WAITING_CHECK_MS = 15000

// Settles with `null` when the vigilance stops: a frame whose answer never comes must not keep the camera
// and the emitter lock.
const untilAborted = (promise, signal) => Promise.race([
  promise,
  new Promise((resolve) => signal.addEventListener('abort', () => resolve(null)))
])

// C13.6 (RN97): a hidden page sends no images; Sun covers it without an incident until the next analysed frame.
const pageHidden = () => document.visibilityState === 'hidden'

// The wait between frames also ends as soon as the page gets hidden, so Sun hears PAGE_HIDDEN at once.
const waitFor = (ms, signal) => new Promise((resolve) => {
  const timer = setTimeout(done, ms)
  function onVisibility () {
    if (pageHidden()) done()
  }
  function done () {
    clearTimeout(timer)
    document.removeEventListener('visibilitychange', onVisibility)
    resolve()
  }
  document.addEventListener('visibilitychange', onVisibility)
  signal.addEventListener('abort', done)
})

const untilVisible = (signal) => new Promise((resolve) => {
  function onVisibility () {
    if (!pageHidden()) done()
  }
  function done () {
    document.removeEventListener('visibilitychange', onVisibility)
    resolve()
  }
  if (!pageHidden()) {
    resolve()
    return
  }
  document.addEventListener('visibilitychange', onVisibility)
  signal.addEventListener('abort', done)
})

// Story 213 — camera vigilance guard. Lives in the app shell (MainContainer), above top bar, menu and
// routes: it is not a MOS widget and no layout can remove it (CU10 step 1).
//
// It only wakes up with a 423 (any call) or with the `mic-vision-watch` mark (C1 steps 2-4): for a user
// without vision it makes no request and never touches the camera (CA10). While Sun says the
// session is covered, MIC content is UNMOUNTED, not hidden (RN21): removing the overlay with the browser
// inspector shows nothing, and uncovering mounts it again so it reloads its data (RN24).
//
// v2 (C7.1): in mode SENSITIVE_ONLY only the sensitive boundaries are unmounted (`SensitiveBoundary`, through
// VisionContext); with no boundary mounted for 2 s while uncovered, the emitting tab asks Sun to leave.
//
// `visionEnabled` is frontEndConfig.vision.enabled, which Sun sends only with VISION_ENABLED on (C11.3, H-1): only then
// does the guard ask for the marked screens. Without a vigilance the top bar shows nothing (S217, PO 2026-10-22). The
// guard does not read frontEndConfig itself (S96).
//
// `startCovered` and `reloadWhenUncovered` serve the start-up guard (S142): it covers from the first render
// and, as the start-up requests that got the 423 never settle, reloads the page once Sun uncovers.
//
// `scope` is frontEndConfig.vision.scope (C13.1, C13.7): with ALL_MIC the guard covers and asks Sun for the session on
// entering MIC, which makes the vigilance be born and turns the camera on without waiting for a 423 (S262), and it never
// asks to leave (S263). With SENSITIVE everything is as before the scope addendum (CA38).
// C14.3 (PO, 2026-10-22): the profiles with a way out of the cover to Camera control.
const PROFILES_WITH_WAY_OUT = ['ADMIN', 'SUPERADMIN']
const COVERED_BOUNDARIES_WITHOUT_STATE = Object.freeze({ ...COVERED_WITHOUT_STATE, mode: 'SENSITIVE_ONLY' })

// `coveredPage` ({ path, element }) is Camera control: covered, an ADMIN or SUPERADMIN on that route gets it mounted on its
// own (no top bar, no menu, nothing of MIC); any other route shows the overlay again (C14.3, S285, S286).
const VisionGuard = ({ children, visionEnabled = false, scope = 'SENSITIVE', unwatchedShowsProtected = false, startCovered = false, reloadWhenUncovered = false, coveredPage = {} }) => {
  const { user, userLogout } = useUser()
  const profileCode = (user && user.profile && user.profile.code) || ''
  const canGoToCameraControl = PROFILES_WITH_WAY_OUT.includes(profileCode) && Boolean(coveredPage.path)
  const location = useLocation()
  const allMic = visionEnabled && scope === 'ALL_MIC'
  const [active, setActive] = useState(() => startCovered || hasVisionMark())
  const [vision, setVision] = useState(startCovered || allMic ? COVERED_WITHOUT_STATE : NOT_WATCHED_STATE)
  const [cameraFailure, setCameraFailure] = useState('')
  const [boundaryCount, setBoundaryCount] = useState(0)
  const [markedScreens, setMarkedScreens] = useState([])
  // A screen whose request got the 423 never settles: uncovering must mount it again even in SENSITIVE_ONLY. Not when a
  // boundary was mounted: the 423 is then that boundary's, and it mounts its own content again when uncovered; remounting
  // the whole of MIC would close what the user opened (a panel, a window) and leave no boundary (reopening 3, LTT201).
  const [contentKey, setContentKey] = useState(0)
  const unsettledRequestRef = useRef(false)
  const mountedBoundariesRef = useRef(0)
  // S336 (B-C16-2): waiting with the setting NO, the route of a screen whose 423 found no boundary is the placeholder.
  const [blurredPath, setBlurredPath] = useState('')
  const visionRef = useRef(NOT_WATCHED_STATE)
  const pathnameRef = useRef(location.pathname)
  pathnameRef.current = location.pathname
  const emitterRef = useRef(false)
  const channelRef = useRef({ post: () => {}, close: () => {} })
  const navigate = useNavigate()

  useEffect(() => {
    if (!visionEnabled) {
      return
    }
    let cancelled = false
    getVisionScreens()
      .then((screens) => {
        if (!cancelled && Array.isArray(screens)) {
          setMarkedScreens(screens.filter((screen) => screen.sensitive === true).map((screen) => screen.key))
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [visionEnabled])

  // frontEndConfig may arrive after the shell mounted: ALL_MIC then covers until Sun says otherwise. With «Protected content»
  // (C16.7, L1, S333) the vigilance is also asked for on entering MIC, but with no overlay: what is not protected is usable
  // at once and the boundaries wait for the state, as always.
  useEffect(() => {
    if (!visionEnabled) {
      return
    }
    if (allMic) {
      setVision((previous) => (previous.watched || previous.covered ? previous : COVERED_WITHOUT_STATE))
    }
    setActive(true)
  }, [visionEnabled, allMic])

  const showState = useCallback((state) => {
    setVision((previous) => (sameVisionState(previous, state) ? previous : state))
  }, [])
  visionRef.current = vision

  // A VisionStateDto from Sun or from the emitting tab.
  const applyState = useCallback((dto) => {
    const state = readVisionState(dto)
    if (reloadWhenUncovered && !state.covered) {
      window.location.reload()
      return
    }
    if (!state.watched) {
      clearVisionMark()
      // C16.7: waiting (no slot, released or no camera) keeps the guard asking every 15 s, with the camera off.
      if (state.waiting) {
        showState(state)
        return
      }
      setBlurredPath('')
      setActive(false)
      showState(NOT_WATCHED_STATE)
      return
    }
    if (!state.covered) {
      setBlurredPath('')
      if (unsettledRequestRef.current) {
        unsettledRequestRef.current = false
        setContentKey((key) => key + 1)
      }
    }
    showState(state)
  }, [showState, reloadWhenUncovered])

  useEffect(() => {
    const channel = openVisionChannel(applyState)
    channelRef.current = channel
    return () => channel.close()
  }, [applyState])

  useEffect(() => subscribeVisionSignals(() => {
    const ownedByBoundary = mountedBoundariesRef.current > 0
    if (!ownedByBoundary) {
      unsettledRequestRef.current = true
      // Waiting with NO, Sun answered a protected piece with a 423 (C16.10): its screen is the placeholder (S336).
      if (visionRef.current.waiting && !unwatchedShowsProtectedRef.current) {
        setBlurredPath(pathnameRef.current)
      }
    }
    // Before Sun says the mode, a 423 met by a mounted boundary covers only the boundaries: covering as in ALL would unmount
    // the whole of MIC, and what the user opened with it (reopening 3, LTT201). If Sun says ALL, its answer brings the overlay.
    const withoutState = ownedByBoundary ? COVERED_BOUNDARIES_WITHOUT_STATE : COVERED_WITHOUT_STATE
    // A session waiting for a camera is not covered by a 423 (C16.10: Sun answers it without a vigilance); it stays waiting.
    setVision((previous) => (previous.covered || previous.waiting ? previous : previous.watched ? { ...previous, covered: true } : withoutState))
    setActive(true)
  }), [])
  const unwatchedShowsProtectedRef = useRef(unwatchedShowsProtected)
  unwatchedShowsProtectedRef.current = unwatchedShowsProtected

  // Sends frames while Sun watches; it settles with the last state Sun gave (or `null` if it stopped otherwise). `firstCamera`
  // is a camera already open (the retry of S340).
  const emitFrames = useCallback(async (signal, firstCamera = null) => {
    let camera = firstCamera
    let waitMs = FRAME_RETRY_MS
    let lastState = null
    try {
      while (!signal.aborted) {
        if (pageHidden()) {
          // S267, S112: tell Sun once, let the camera go, and wait for the page to be visible again.
          if (camera) {
            camera.stop()
            camera = null
          }
          let hiddenDto = null
          try {
            hiddenDto = await untilAborted(sendVisionFrame('', 'PAGE_HIDDEN'), signal)
          } catch (exception) {
            hiddenDto = null
          }
          if (signal.aborted) {
            break
          }
          if (hiddenDto) {
            applyState(hiddenDto)
            channelRef.current.post(hiddenDto)
            lastState = readVisionState(hiddenDto)
            if (!lastState.watched) {
              break
            }
          }
          await untilVisible(signal)
          continue
        }
        let failure = ''
        let image = ''
        if (!camera) {
          const opened = await openVisionCamera()
          failure = opened.failure
          camera = opened.camera || null
        }
        if (camera && camera.failure()) {
          failure = camera.failure()
          camera.stop()
          camera = null
        }
        if (camera) {
          try {
            image = await camera.capture()
          } catch (exception) {
            failure = 'NOT_READABLE'
            camera.stop()
            camera = null
          }
        }
        if (signal.aborted) {
          break
        }
        setCameraFailure(failure)
        let dto = null
        try {
          dto = await untilAborted(sendVisionFrame(image, failure), signal)
        } catch (exception) {
          dto = null
        }
        if (signal.aborted) {
          break
        }
        if (dto) {
          applyState(dto)
          channelRef.current.post(dto)
          const state = readVisionState(dto)
          lastState = state
          if (!state.watched) {
            break
          }
          // RN12, C7.1: exactly what Sun says; Sun applies the backoff (RN26).
          waitMs = state.nextFrameAfterMs
        }
        await waitFor(waitMs, signal)
      }
    } finally {
      if (camera) {
        camera.stop()
      }
    }
    return lastState
  }, [applyState])

  // C16.10 (S340): a session without a camera tries it again; with an image Sun puts it back in the queue (or watches it).
  // Settles with Sun's state and the open camera, or `null` when there is still no camera.
  const tryCameraAgain = useCallback(async (signal) => {
    const opened = await openVisionCamera()
    if (!opened.camera) {
      setCameraFailure(opened.failure)
      return null
    }
    let image = ''
    try {
      image = await opened.camera.capture()
    } catch (exception) {
      image = ''
    }
    if (!image || signal.aborted) {
      opened.camera.stop()
      return null
    }
    setCameraFailure('')
    let dto = null
    try {
      dto = await untilAborted(sendVisionFrame(image, ''), signal)
    } catch (exception) {
      dto = null
    }
    if (!dto || signal.aborted) {
      opened.camera.stop()
      return null
    }
    applyState(dto)
    channelRef.current.post(dto)
    const state = readVisionState(dto)
    if (!state.watched) {
      opened.camera.stop()
      return { state, camera: null }
    }
    return { state, camera: opened.camera }
  }, [applyState])

  useEffect(() => {
    if (!active) {
      return
    }
    const controller = new AbortController()
    setVisionMark()
    const signal = controller.signal
    const askSession = async () => {
      let session = null
      try {
        session = await getVisionSession()
      } catch (exception) {
        return null
      }
      if (signal.aborted) {
        return null
      }
      applyState(session)
      return readVisionState(session)
    }
    // Only the emitting tab opens the camera (the lock of the frames, RN17): `work` runs while holding it.
    const asEmitter = async (work) => {
      let result = null
      await runAsEmitter(signal, async () => {
        emitterRef.current = true
        try {
          result = await work()
        } finally {
          emitterRef.current = false
        }
      })
      return result
    }
    // C16.10 (S340): without a camera, try it again now and at each 15 s check, inside the lock; with an image and a slot,
    // the frames follow with that same camera. Settles with the state the retry ended in.
    const retryCamera = () => asEmitter(async () => {
      let current = null
      while (!signal.aborted) {
        const retried = await tryCameraAgain(signal)
        if (retried && retried.state.watched) {
          return emitFrames(signal, retried.camera)
        }
        current = retried ? retried.state : current
        if (current && (!current.waiting || current.unwatchedReason !== 'NO_CAMERA')) {
          return current
        }
        await waitFor(WAITING_CHECK_MS, signal)
        current = await askSession()
        if (!current || current.watched || !current.waiting || current.unwatchedReason !== 'NO_CAMERA') {
          return current
        }
      }
      return null
    })
    // Watched: frames until Sun stops watching. Waiting (C16.7): ask again every 15 s and, without a camera, try it again.
    const watch = async () => {
      let state = await askSession()
      while (state && !signal.aborted) {
        if (state.watched) {
          state = await asEmitter(() => emitFrames(signal))
          if (!state || !state.waiting) {
            return
          }
          await waitFor(WAITING_CHECK_MS, signal)
          state = await askSession()
          continue
        }
        if (!state.waiting) {
          return
        }
        if (state.unwatchedReason === 'NO_CAMERA') {
          state = await retryCamera()
          continue
        }
        await waitFor(WAITING_CHECK_MS, signal)
        state = await askSession()
      }
    }
    watch()
    return () => controller.abort()
  }, [active, applyState, emitFrames, tryCameraAgain])

  const leave = useCallback(async () => {
    let dto = null
    try {
      dto = await leaveVision()
    } catch (exception) {
      return
    }
    applyState(dto)
    channelRef.current.post(dto)
  }, [applyState])

  // Uncovering after the full-screen overlay (mode ALL) mounts MIC again without what the user had open (S104): that is not
  // leaving either, so while the user stays on that route with no boundary the guard does not ask to leave; it does again
  // once a boundary is mounted (the user opened it again) or on another route (PO 2026-10-26, LTT201).
  const [reopenPendingOn, setReopenPendingOn] = useState('')
  const coveredEverythingRef = useRef(false)
  useEffect(() => {
    if (!vision.watched) {
      setReopenPendingOn('')
    } else if (coveredEverythingRef.current && !vision.covered) {
      setReopenPendingOn(pathnameRef.current)
    }
    coveredEverythingRef.current = vision.covered && vision.mode !== 'SENSITIVE_ONLY'
  }, [vision.watched, vision.covered, vision.mode])
  useEffect(() => {
    if (boundaryCount > 0) {
      setReopenPendingOn('')
    }
  }, [boundaryCount])

  // RN9: leaving sensitive content. Only while uncovered (a covered screen has its boundaries unmounted, which
  // is not leaving) and only from the emitting tab. With ALL_MIC there is no leaving at all (RN94, S263).
  const reopenPendingHere = reopenPendingOn !== '' && reopenPendingOn === location.pathname
  useEffect(() => {
    if (allMic || !vision.watched || vision.covered || boundaryCount > 0 || !emitterRef.current || reopenPendingHere) {
      return
    }
    const timer = setTimeout(leave, LEAVE_AFTER_MS)
    return () => clearTimeout(timer)
  }, [allMic, vision.watched, vision.covered, boundaryCount, leave, reopenPendingHere])

  const registerBoundary = useCallback(() => {
    mountedBoundariesRef.current += 1
    setBoundaryCount((count) => count + 1)
    return () => {
      mountedBoundariesRef.current -= 1
      setBoundaryCount((count) => count - 1)
    }
  }, [])

  // H-v2-16, DB-4: Back (offered only with `canLeave`) returns to where the user came from and asks Sun to leave.
  const back = useCallback(() => {
    navigate(-1)
    leave()
  }, [navigate, leave])

  const goToCameraControl = useCallback(() => {
    navigate(coveredPage.path)
  }, [navigate, coveredPage.path])

  // C16.10 (RN147, supuesto 8): not watched with «Protected content» and the setting NO, the protected pieces are the blurred
  // placeholder in both modes and nothing else covers; with YES they show as with the vision off.
  const protectedHidden = vision.covered || (vision.waiting && !unwatchedShowsProtected)
  const context = useMemo(
    () => ({ state: vision, cameraFailure, markedScreens, visionEnabled, protectedHidden, blurredPath, registerBoundary, back, canGoToCameraControl, goToCameraControl }),
    [vision, cameraFailure, markedScreens, visionEnabled, protectedHidden, blurredPath, registerBoundary, back, canGoToCameraControl, goToCameraControl]
  )

  const logout = () => {
    clearVisionMark()
    setActive(false)
    userLogout()
  }

  // C7.1: mode ALL covers the whole app; SENSITIVE_ONLY leaves it mounted and the boundaries cover themselves. A session
  // waiting with «All of MIC» does not use MIC (C16.10, S339): the overlay says it waits for a camera, or asks for one.
  const waitingOnAllMic = vision.waiting && allMic
  const coversEverything = (vision.covered && vision.mode !== 'SENSITIVE_ONLY') || waitingOnAllMic
  const onCoveredPage = coversEverything && canGoToCameraControl && location.pathname === coveredPage.path
  const overlayState = waitingOnAllMic
    ? { ...vision, covered: true, coverReason: vision.unwatchedReason === 'NO_CAMERA' ? 'NO_CAMERA' : 'WAITING', canLeave: false }
    : vision

  return (
    <VisionContext.Provider value={context}>
      {/* The page paints its own way back ("Back to where you were", Jupiter B). */}
      {onCoveredPage && <Box data-testid='covered-camera-control'>{coveredPage.element}</Box>}
      {coversEverything && !onCoveredPage && <VisionOverlay state={overlayState} cameraFailure={cameraFailure} onBack={back} onLogout={logout} />}
      {!coversEverything && <React.Fragment key={contentKey}>{children}</React.Fragment>}
      {/* C11.3 (S218): in mode ALL the top bar is unmounted, so the chip of the covered screen goes over the overlay. */}
      {coversEverything && (
        <Box data-testid='vision-overlay-header' sx={{ position: 'fixed', top: 16, right: 24, zIndex: (theme) => theme.zIndex.modal + 2 }}>
          <VisionChip />
        </Box>
      )}
      {vision.watched && vision.warning === 'CROWD' && <VisionCrowdWarning state={vision} />}
    </VisionContext.Provider>
  )
}

export default VisionGuard
